import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  initKeycloak, 
  getIdToken, 
  clearStoredToken, 
  initTokenFromStorage,
  logout as keycloakLogout,
  getCurrentUser,
  getUserRoles,
  sendPasswordResetEmail as keycloakSendPasswordResetEmail,
  isAuthenticated,
  isMagicLinkAuth
} from '../utils/keycloak';
import { UserRole, AppUser } from '../types';
import axios from 'axios';
import { getBackendUrl } from '../utils/utils';
import { syncUserTimezone } from '../services/userService';
import { startDemoProxy as apiStartDemoProxy, stopDemoProxy as apiStopDemoProxy } from '../services/demoProxyService';
import { createLogger } from '../utils/logger';
import { retryPendingCompletes } from '../hooks/pending-complete';

const logger = createLogger('AuthContext');

interface AuthContextType {
  currentUser: AppUser | null;
  token: string | null;
  loading: boolean;
  logout: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  userRole: UserRole | null;
  error: string | null;
  isOffline: boolean;
  userData: any | null;
  patchUserData: (patch: Partial<AppUser>) => void;
  refreshAuth: () => Promise<void>;
  /** Re-fetches total_sessions_completed + access_end_date without waiting for cache to expire. */
  refreshSessionCount: () => Promise<void>;
  /** Optimistically increments total_sessions_completed by 1 immediately (no network call). */
  incrementSessionCount: () => void;
  isMagicLinkAuth: boolean;
  /** True when the user is a Google Marketplace subscriber (has marketplace_account_id). */
  isMarketplaceUser: boolean;
  /** True when the marketplace user's org name is still the auto-generated default. */
  orgNamePending: boolean;
  /** True when the authenticated non-admin user has no organization assigned. */
  noOrganization: boolean;
  /** True when the user is in trial mode (access_end_date is set). */
  isTrialUser: boolean;
  /** Number of sessions the trial user has used. */
  trialSessionsUsed: number;
  /** Maximum sessions allowed during trial. */
  trialSessionLimit: number;
  /** True when the trial is over (sessions exhausted or date passed). */
  isTrialExhausted: boolean;
  /** True when the user's Marketplace subscription has been cancelled/deleted. */
  subscriptionCancelled: boolean;
  /**
   * The user's real role, ignoring any active demo proxy. Equals `userRole` unless a
   * super admin is "viewing as" a lower role — gate the role switcher on this, never
   * on `userRole`, or a proxied super admin loses the way back out.
   */
  realRole: UserRole | null;
  /** True while a super admin is viewing the app as a lower role (see demoProxyService). */
  isProxyActive: boolean;
  /** Start/re-point the demo proxy, then refresh auth so the whole app re-reads its role. */
  startDemoProxy: (role: UserRole, organizationId: string) => Promise<void>;
  /** End the demo proxy and restore the super-admin view. */
  stopDemoProxy: () => Promise<void>;
}

export const TRIAL_SESSION_LIMIT = 5;

// Largest delay setTimeout accepts (2^31 - 1 ms); longer ones fire immediately.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Cache for authentication state
const authCache = {
  currentUser: null as AppUser | null,
  token: null as string | null,
  userRole: null as UserRole | null,
  realRole: null as UserRole | null,
  userData: null as any | null,
  lastUpdated: 0,
  isInitialized: false,
  isFirstLoad: true
};

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// User data cache
const userDataCache: {
  [uid: string]: {
    data: { role: UserRole; userData: any };
    expirationTime: number;
  };
} = {};

const USER_DATA_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Map Keycloak roles to application UserRole
 */
const mapKeycloakRoleToUserRole = (roles: string[]): UserRole => {
  // Priority order: super_admin > admin > trainer > trainee
  if (roles.includes('super_admin')) return 'super_admin';
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('trainer')) return 'trainer';
  return 'trainee';
};

/**
 * `loadFailed` marks the JWT-role fallback used when /user/{uid} could not be reached
 * after every retry: the user's org and profile are unknown, not absent.
 */
const fetchUserData = async (
  uid: string,
  token: string,
  retryCount = 0,
): Promise<{ role: UserRole; userData: any; loadFailed?: boolean }> => {
  const now = Date.now();
  try {
    // Check cache first, but validate the cached role against the current JWT.
    // If the JWT has a different (higher) role than the cache, the user was
    // promoted since the cache was populated — bust it so we fetch fresh data.
    const cachedData = userDataCache[uid];
    if (cachedData && cachedData.expirationTime > now) {
      const jwtRoles = getUserRoles();
      const jwtRole = mapKeycloakRoleToUserRole(jwtRoles);
      // Compare against the *real* role: with a demo proxy active, cached `role` is
      // the proxied one and would never match the JWT, busting the cache on every
      // single fetch. real_role is what the JWT actually speaks to.
      const cachedRealRole = (cachedData.data.userData?.real_role as UserRole) || cachedData.data.role;
      if (jwtRoles.length > 0 && jwtRole !== cachedRealRole) {
        // JWT and cache disagree — cache is stale, fall through to API fetch
        delete userDataCache[uid];
      } else {
        console.log('Using cached user data for UID:', uid);
        return cachedData.data;
      }
    }

    console.log('Fetching user data for UID:', uid);

    // Check for marketplace org assignment
    const marketplaceOrgId = sessionStorage.getItem('marketplace_org_id');
    const queryParams = marketplaceOrgId ? `?marketplace_org_id=${marketplaceOrgId}` : '';

    // A failure here goes to the retry below. It used to be caught on the spot and the
    // JWT-role fallback cached for 5 minutes, so the retries never ran and one slow
    // first-login lookup (Keycloak, first-login writes) left the user with no profile
    // and no org until the cache ran out.
    const response = await axios.get(`${getBackendUrl()}/user/${uid}${queryParams}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    // Clear the marketplace org ID after successful assignment
    if (marketplaceOrgId) {
      sessionStorage.removeItem('marketplace_org_id');
    }
    const data = response.data as { role?: string };
    console.log('User data from API:', data.role);

    const userData = {
      role: (data.role || 'trainee') as UserRole,
      userData: data
    };

    // Cache the data
    userDataCache[uid] = {
      data: userData,
      expirationTime: now + USER_DATA_CACHE_DURATION
    };

    return userData;
  } catch (error) {
    console.error(`Error fetching user data (attempt ${retryCount + 1}):`, error);

    // Retry only what can succeed on a second try: no response at all, 5xx, timeouts
    // and rate limits, plus 404 — the backend answers 404 when its Keycloak lookup times
    // out. 401/403 fail the same way again with the same token, so they fall through.
    const status: number | undefined = (error as any)?.response?.status;
    const retryable = !status || status >= 500 || [404, 408, 429].includes(status);
    if (retryable && retryCount < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return fetchUserData(uid, token, retryCount + 1);
    }

    // Final fallback - use role from Keycloak token. Not cached, so the next auth
    // check asks the API again instead of reusing the failure.
    const roles = getUserRoles();
    const role = mapKeycloakRoleToUserRole(roles);

    return {
      role: role as UserRole,
      userData: null,
      loadFailed: true,
    };
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(authCache.currentUser);
  const [token, setToken] = useState<string | null>(authCache.token);
  const [loading, setLoading] = useState(authCache.isFirstLoad);
  const [userRole, setUserRole] = useState<UserRole | null>(authCache.userRole);
  const [realRole, setRealRole] = useState<UserRole | null>(authCache.realRole);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(false);
  const [userData, setUserData] = useState<any | null>(authCache.userData);
  const [isMagicLinkAuth, setIsMagicLinkAuth] = useState(false);
  const [isMarketplaceUser, setIsMarketplaceUser] = useState(false);
  const [noOrganization, setNoOrganization] = useState(false);
  const [orgNamePending, setOrgNamePending] = useState(false);
  const [subscriptionCancelled, setSubscriptionCancelled] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);

        // Try to restore token from localStorage before initializing Keycloak
        const storedToken = initTokenFromStorage();
        if (storedToken) {
          setToken(storedToken);
        }

        // Initialize Keycloak
        const authenticated = await initKeycloak(
          // On auth success — intentionally NOT calling updateAuthState here.
          // initKeycloak calls this callback synchronously with onAuthSuccess?.()
          // (no await), so an async updateAuthState here would race with the
          // awaited updateAuthState in the `if (authenticated)` block below.
          // Let the single awaited call below be the sole owner of auth state.
          () => {
            console.log('Keycloak authentication successful');
          },
          // On auth error
          (error) => {
            console.error('Keycloak authentication error:', error);
            // Only show error for actual connection issues, not 401 during check-sso
            if (error.message?.includes('Cannot connect to Keycloak server')) {
              setError('Cannot connect to authentication server. Please check your network connection.');
            } else if (error.message?.includes('Keycloak server authentication failed')) {
              setError('Unable to connect to authentication server. Please check if Keycloak is running.');
            } else {
              // For other errors (like 401 during check-sso), don't show error - this is normal
              console.log('Authentication check completed (user not logged in)');
            }
            setLoading(false);
          }
        );

        if (authenticated) {
          await updateAuthState();
        } else {
          console.log('User is not authenticated');
          clearAuthState();
          setLoading(false);
        }

        // Set up periodic token check (every 30 seconds)
        const tokenCheckInterval = setInterval(async () => {
          if (isAuthenticated()) {
            try {
              const newToken = await getIdToken();
              setToken(newToken);
            } catch (error) {
              console.error('Failed to refresh token:', error);
              clearAuthState();
            }
          }
        }, 30000);

        return () => {
          clearInterval(tokenCheckInterval);
        };
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
        setError('Failed to initialize authentication. Please refresh the page.');
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const updateAuthState = async () => {
    const now = Date.now();

    // If there is a pending marketplace claim (user just came from GCP Subscribe
    // redirect), bust the userDataCache so we never serve stale 'cancelled' org
    // status to a re-subscriber. Without this, the 30-min authCache would return
    // subscriptionCancelled=true from a previous session before the claim gate
    // runs and corrects the org status.
    const _pendingMpProject = localStorage.getItem('pending_marketplace_customer_project');
    if (_pendingMpProject && authCache.currentUser?.id) {
      delete userDataCache[authCache.currentUser.id];
    }

    // Check if we have valid cached data.
    // Also bust the authCache if the userDataCache for this user has expired — this
    // ensures subscription_status and org data are re-fetched within 5 minutes even
    // if the 30-min authCache is still warm. Without this, a cancelled subscriber
    // could stay logged in for up to 30 min after cancellation.
    const _cachedUid = authCache.currentUser?.id;
    const _userDataStale = _cachedUid
      ? !userDataCache[_cachedUid] || userDataCache[_cachedUid].expirationTime <= now
      : false;
    if (authCache.isInitialized &&
      authCache.lastUpdated + CACHE_DURATION > now &&
      authCache.currentUser &&
      !_userDataStale) {
      setCurrentUser(authCache.currentUser);
      setToken(authCache.token);
      setUserRole(authCache.userRole);
      setRealRole(authCache.realRole);
      setUserData(authCache.userData);
      // Keep localStorage in sync with the cached role so the sidebar (which falls
      // back to localStorage) never disagrees with the reactive AuthContext state.
      if (authCache.userRole) {
        localStorage.setItem('role', authCache.userRole);
      }
      const _cachedIsMarketplace = !!(authCache.userData as any)?.marketplace_account_id;
      const _cachedOrgNamePending = !!((authCache.userData as any)?.marketplace_org_name_pending);
      const _cachedOrgStatus = (authCache.userData as any)?.organization?.subscription_status || '';
      setIsMarketplaceUser(_cachedIsMarketplace);
      setOrgNamePending(_cachedIsMarketplace && _cachedOrgNamePending);
      setSubscriptionCancelled(
        _cachedIsMarketplace && ['cancelled', 'deleted', 'past_due'].includes(_cachedOrgStatus)
      );
      if (!(authCache.userData as any)?.organization_id && authCache.userRole !== 'super_admin') {
        setNoOrganization(true);
      } else {
        setNoOrganization(false);
      }
      setLoading(false);
      return;
    }

    try {
      const user = getCurrentUser();
      if (!user) {
        clearAuthState();
        setLoading(false);
        return;
      }

      setCurrentUser({
        id: user.id,
        email: user.email || '',
        display_name: user.display_name || ''
      });
      setError(null);

      // Get token
      let idToken: string;
      try {
        idToken = await getIdToken();
      } catch (tokenError: any) {
        if (tokenError.message === 'MAGIC_LINK_EXPIRED' || tokenError.message === 'MAGIC_LINK_REFRESH_ERROR' || tokenError.message === 'MAGIC_LINK_REFRESH_FAILED') {
          console.log('Magic link token expired or refresh failed, redirecting to session expired page');
          clearAuthState();
          setLoading(false);
          window.location.href = '/session-expired';
          return;
        }
        throw tokenError; // Re-throw other token errors
      }
      setToken(idToken);

      // Fetch user data
      try {
        const { role, userData: _fetchedUserDataInit, loadFailed } = await fetchUserData(user.id, idToken);
        // Reassignable: the marketplace org-link self-heal below may repoint the user's
        // organization and refetch, so all downstream state uses the corrected data.
        let fetchedUserData = _fetchedUserDataInit;

        // ── Marketplace claim gate ───────────────────────────────────────────
        // Fire /marketplace/claim when we have a customer_project (from localStorage
        // after the GCP redirect, or from the marketplace_customer_project Keycloak
        // attribute) AND the claim has not yet fully completed.
        //
        // "Claim complete" requires BOTH marketplace_account_id AND organization_id.
        //
        // marketplace_account_id alone is NOT sufficient because the re-subscription
        // Pub/Sub handler sets marketplace_account_id in Keycloak before org_id and
        // role are provisioned.  If we treat account_id-only as done, the user lands
        // with no org and default role on every repurchase.
        //
        // Requiring org_id too means:
        //   - Re-sub user (account_id set, org_id missing) → /claim fires → path 1b
        //     re-provisions the missing org_id and role. ✓
        //   - Manually provisioned user (org_id set, no account_id) → /claim fires →
        //     account_id gets set. ✓ (same behaviour as before for this case)
        //   - Fully provisioned user (both set, pending matches existing, org active) → /claim skipped. ✓
        //   - User already provisioned for sub A but pending is sub B → pendingCustomerProject
        //     !== existingAccountId → claimAlreadyDone=false → /claim fires for sub B. ✓
        //   - Re-subscriber (same billing account, org cancelled, new purchase) →
        //     pending matches existing BUT org is cancelled → claimAlreadyDone=false → /claim fires. ✓
        const pendingCustomerProject =
          localStorage.getItem('pending_marketplace_customer_project') ||
          (fetchedUserData as any)?.marketplace_customer_project ||
          null;
        const existingAccountId = (fetchedUserData as any)?.marketplace_account_id;
        const _currentOrgStatus = (fetchedUserData as any)?.organization?.subscription_status || '';
        const _isOrgCancelled = ['cancelled', 'deleted', 'past_due'].includes(_currentOrgStatus);
        // "Already done" requires both attrs set AND that the pending customer_project
        // matches the already-claimed one. If they differ, it's a new/different
        // subscription — force the claim even if the user looks fully provisioned.
        // Exception: if org is cancelled and a new pending project is present, the user
        // re-purchased — force the claim so it can sync the org back to pending/active.
        const claimAlreadyDone =
          !!existingAccountId
          && !!(fetchedUserData as any)?.organization_id
          && (!pendingCustomerProject ||
              (pendingCustomerProject === existingAccountId && !_isOrgCancelled));
        if (pendingCustomerProject && !claimAlreadyDone) {
          try {
            await axios.post(
              `${getBackendUrl()}/marketplace/claim`,
              { customer_project: pendingCustomerProject },
              { headers: { Authorization: `Bearer ${idToken}` } }
            );
            // Claim succeeded — clear the pending flag and reload so Keycloak
            // issues a fresh token that includes the new organization_id attribute.
            localStorage.removeItem('pending_marketplace_customer_project');
            window.location.reload();
            return; // updateAuthState will run again after the reload
          } catch (claimError: any) {
            const claimStatus = claimError.response?.status;
            const detail = claimError.response?.data?.detail || 'Failed to activate your subscription.';
            console.error('Marketplace claim failed:', claimError);

            // Permanent failures: clear localStorage so we don't retry forever.
            // 402 "subscription_processing" is TEMPORARY — keep localStorage so the
            // claim gate re-fires automatically when the user signs in again after
            // GCP activates the subscription.
            const isProcessing = claimStatus === 402 && detail === 'subscription_processing';
            if (!isProcessing && (claimStatus === 402 || claimStatus === 404 || claimStatus === 409)) {
              localStorage.removeItem('pending_marketplace_customer_project');
            }

            // 402 "subscription_processing" — GCP accepted the offer but billing
            // isn't confirmed yet (PENDING). Show a transient "processing" notice,
            // NOT the SubscriptionCancelledPage (this is temporary, not permanent).
            if (claimStatus === 402 && detail === 'subscription_processing') {
              setError(
                'Your subscription is being confirmed by Google. ' +
                'This usually takes just a few minutes. Please try signing in again shortly.'
              );
              setNoOrganization(true);
              setLoading(false);
              return;
            }

            // 402 (non-processing) means the subscription is cancelled. Show the
            // SubscriptionCancelledPage instead of a raw error string so the
            // user sees a clear "reactivate on GCP" message.
            if (claimStatus === 402 && (fetchedUserData as any)?.marketplace_account_id) {
              setIsMarketplaceUser(true);
              setSubscriptionCancelled(true);
              setLoading(false);
              return;
            }

            // 409 — two possible causes, both permanent:
            //   a) Cross-subscription: user belongs to a different active subscription.
            //   b) Duplicate identity: user registered with email+password and is now
            //      trying "Continue with Google" with the same address — Keycloak
            //      created a second identity.  Backend returns a clear message for both.
            if (claimStatus === 409) {
              setNoOrganization(true);
              setError(detail);
              setLoading(false);
              return;
            }

            // Surface the error to the user — they need to act on it.
            // Also set noOrganization so OrganizationGuard blocks the app for users
            // who have no org (claim failed = no org provisioned yet).
            if (!(fetchedUserData as any)?.organization_id) {
              setNoOrganization(true);
            }
            setError(detail);
            setLoading(false);
            return;
          }
        } else if (pendingCustomerProject && claimAlreadyDone) {
          // Claim not needed (user is fully provisioned and org is healthy).
          // Clear the stale localStorage key so Fix #5 (cache bust) doesn't fire
          // on every subsequent login after a successful re-subscription.
          localStorage.removeItem('pending_marketplace_customer_project');
        }
        // ── End marketplace claim gate ────────────────────────────────────────

        // ── Marketplace org-link self-heal ────────────────────────────────────
        // A buyer can reach here with an empty/stale organization_id — e.g. a
        // re-purchase that named a new org, or a "Continue with Google" signup
        // abandoned before the final "Sign Up" (register-google never ran). That
        // leaves the Users page showing "No organization" while the entitlement is
        // really linked to an org. Repoint to the entitlement's org. The backend is
        // idempotent and buyer-scoped, and only heals empty/dangling/foreign links,
        // so a valid org (incl. team members) is never touched — a no-op otherwise.
        if ((fetchedUserData as any)?.marketplace_account_id) {
          try {
            const healResp = await axios.post(
              `${getBackendUrl()}/marketplace/reconcile-org-link`,
              {},
              { headers: { Authorization: `Bearer ${idToken}` } }
            );
            if ((healResp.data as any)?.changed) {
              // Org link corrected — bust the per-user cache and refetch so the
              // Users, Profile and Subscription views all agree on the new org.
              delete userDataCache[user.id];
              // Keep the profile that already loaded if the refetch fails, rather than
              // dropping to no profile and, from there, the no-org wall.
              const refetched = await fetchUserData(user.id, idToken);
              if (!refetched.loadFailed) fetchedUserData = refetched.userData;
            }
          } catch (healErr) {
            // Best-effort: never block login on the heal. Leave state as-is.
            console.warn('Marketplace org-link self-heal failed:', healErr);
          }
        }

        // A profile that could not be loaded says nothing about the org. Treating it as
        // "no organization" sent an existing org admin to the signup / no-org wall; the
        // backend resolves their org from the token, so let the app load instead.
        if (!loadFailed && !(fetchedUserData as any)?.organization_id && role !== 'super_admin') {
          setNoOrganization(true);
        } else {
          setNoOrganization(false);
        }
        if (loadFailed) {
          setError('Failed to fetch user data. Using default role.');
        }

        setUserRole(role);
        // real_role is only sent for super admins; everyone else's real role is their role.
        const effectiveRealRole = ((fetchedUserData as any)?.real_role as UserRole) || role;
        setRealRole(effectiveRealRole);
        setUserData(fetchedUserData);
        setIsOffline(false);

        // Best-effort: keep the profile's stored timezone in sync with the browser's
        // so gamification streak/heatmap day-boundaries track where the user actually is.
        syncUserTimezone(user.id, idToken, (fetchedUserData as any)?.timezone).catch(() => {});

        // Marketplace user detection
        const _isMarketplace = !!(fetchedUserData as any)?.marketplace_account_id;
        const _orgNamePending = !!((fetchedUserData as any)?.marketplace_org_name_pending);
        const _orgStatus = (fetchedUserData as any)?.organization?.subscription_status || '';
        setIsMarketplaceUser(_isMarketplace);
        setOrgNamePending(_isMarketplace && _orgNamePending);
        setSubscriptionCancelled(
          _isMarketplace && ['cancelled', 'deleted', 'past_due'].includes(_orgStatus)
        );

        // Store role in localStorage for later use
        localStorage.setItem('role', role);

        const enrichedUser: AppUser = {
          id: user.id,
          email: user.email || '',
          // Prefer the freshly-fetched value over the JWT's `name` claim, which is only
          // re-evaluated on a new token issuance — otherwise an edited display name
          // wouldn't show up until the user logs out and back in.
          display_name: (fetchedUserData as any)?.display_name || user.display_name || '',
          preferred_name: (fetchedUserData as any)?.preferred_name ?? undefined,
          contact_number: (fetchedUserData as any)?.contact_number ?? undefined,
          location: (fetchedUserData as any)?.location ?? undefined,
          role,
          organization: (fetchedUserData as any)?.organization ?? undefined,
          organization_id: (fetchedUserData as any)?.organization_id ?? undefined,
          marketplace_account_id: (fetchedUserData as any)?.marketplace_account_id ?? undefined,
          marketplace_customer_project: (fetchedUserData as any)?.marketplace_customer_project ?? undefined,
          policy_accepted_version: (fetchedUserData as any)?.policy_accepted_version ?? undefined,
          mic_setup_completed: (fetchedUserData as any)?.mic_setup_completed ?? undefined,
          interactive_tutorial_completed: (fetchedUserData as any)?.interactive_tutorial_completed ?? undefined,
          total_sessions_completed: (fetchedUserData as any)?.total_sessions_completed ?? undefined,
          is_trial: (fetchedUserData as any)?.is_trial ?? undefined,
          access_end_date: (fetchedUserData as any)?.access_end_date ?? undefined,
          access_deadline: (fetchedUserData as any)?.access_deadline ?? undefined,
          is_billing_owner: (fetchedUserData as any)?.is_billing_owner ?? undefined,
          is_org_billing_owner: (fetchedUserData as any)?.is_org_billing_owner ?? undefined,
          can_view_join_requests: (fetchedUserData as any)?.can_view_join_requests ?? undefined,
          invited_by: (fetchedUserData as any)?.invited_by ?? undefined,
          user_from: (fetchedUserData as any)?.user_from ?? undefined,
          real_role: effectiveRealRole,
          proxy_active: !!(fetchedUserData as any)?.proxy_active,
        };
        setCurrentUser(enrichedUser);

        // Update cache
        authCache.currentUser = enrichedUser;
        authCache.token = idToken;
        authCache.userRole = role;
        authCache.realRole = effectiveRealRole;
        authCache.userData = fetchedUserData;
        authCache.lastUpdated = now;
        authCache.isInitialized = true;
        authCache.isFirstLoad = false;
      } catch (userDataError) {
        console.error('Failed to fetch user data:', userDataError);
        
        // Use role from Keycloak token as fallback
        const roles = getUserRoles();
        const role = mapKeycloakRoleToUserRole(roles);
        
        setUserRole(role);
        // No API payload to read a proxy from — fall back to the JWT's own role.
        setRealRole(role);
        setUserData(null);
        setError('Failed to fetch user data. Using default role.');
        
        // Store role in localStorage
        localStorage.setItem('role', role);
      }

      setLoading(false);
    } catch (error) {
      console.error("Failed to update auth state:", error);
      setError('Authentication error. Please try again later.');
      clearAuthState();
      setLoading(false);
    }
  };

  const clearAuthState = () => {
    setCurrentUser(null);
    setToken(null);
    setUserRole(null);
    setRealRole(null);
    setUserData(null);
    setError(null);
    setIsOffline(false);
    setIsMagicLinkAuth(false);
    setIsMarketplaceUser(false);
    setOrgNamePending(false);
    setNoOrganization(false);
    setSubscriptionCancelled(false);

    // Clear module-level caches so the next login always fetches fresh data
    authCache.currentUser = null;
    authCache.token = null;
    authCache.userRole = null;
    authCache.realRole = null;
    authCache.userData = null;
    authCache.lastUpdated = 0;
    authCache.isInitialized = false;

    // Clear per-user data cache so stale org/role data is never reused after logout
    Object.keys(userDataCache).forEach(key => delete userDataCache[key]);
  };

  const logout = async () => {
    // End any demo proxy first: it lives server-side with a multi-hour TTL, so a
    // super admin who logged out mid-demo would otherwise come back as the proxied
    // role on their next login. Best-effort — never block logout on it.
    if (authCache.userData?.proxy_active) {
      try {
        await apiStopDemoProxy();
      } catch (e) {
        logger.warn('Failed to end demo proxy on logout:', e);
      }
    }
    clearAuthState();
    authCache.isFirstLoad = true;
    clearStoredToken();
    await keycloakLogout();
  };

  const sendPasswordResetEmail = async (email: string) => {
    try {
      await keycloakSendPasswordResetEmail(email);
      return Promise.resolve();
    } catch (error) {
      console.error("Error sending password reset email:", error);
      return Promise.reject(error);
    }
  };

  const refreshAuth = async () => {
    // Show loading while refreshing so the UI never renders with a stale/null role.
    setLoading(true);
    // Bust both caches so updateAuthState always fetches fresh data from the API.
    // authCache (30-min TTL) guards the outer updateAuthState fast-return.
    // userDataCache (5-min TTL) guards the inner fetchUserData call.
    // Both must be cleared, otherwise refreshAuth returns stale Keycloak attributes
    // (e.g. marketplace_org_name_pending stays true after the PUT /marketplace/org-name).
    authCache.lastUpdated = 0;
    // currentUser is null when refreshAuth is called from Login.tsx immediately after
    // loginWithCredentials (before updateAuthState has run). Fall back to the Keycloak
    // instance to get the UID so the stale userDataCache entry is reliably cleared.
    const uidToClear = currentUser?.id || getCurrentUser()?.id;
    if (uidToClear) {
      delete userDataCache[uidToClear];
    }
    await updateAuthState();
  };

  // Both actions refresh auth afterwards: the proxy is server-side state, so the
  // role, org, sidebar and route guards only pick it up once user data is re-fetched.
  const startDemoProxy = async (role: UserRole, organizationId: string) => {
    await apiStartDemoProxy(role, organizationId);
    await refreshAuth();
  };

  const stopDemoProxy = async () => {
    await apiStopDemoProxy();
    await refreshAuth();
  };

  // Resend any /gemini/session/{id}/complete call left over from a previous
  // app session (tab closed/crashed before it could be sent — see
  // use-gemini-direct's savePendingComplete). Runs here, at the auth root,
  // rather than inside GeminiDirectContext, because that context only mounts
  // on the /roleplay route — a user who logs back in and lands on the
  // dashboard would otherwise never trigger the retry until the backend's
  // own orphaned-session watchdog catches it minutes later.
  const pendingCompleteSweepDoneRef = useRef(false);
  useEffect(() => {
    if (!token || pendingCompleteSweepDoneRef.current) return;
    pendingCompleteSweepDoneRef.current = true;
    retryPendingCompletes(getBackendUrl(), token, currentUser?.id).catch((err) => {
      logger.warn('Pending /complete retry sweep failed:', err);
    });
  }, [token, currentUser?.id]);

  const refreshSessionCount = useCallback(async () => {
    if (!currentUser?.id || !token) return;
    const userId = currentUser.id;
    try {
      const response = await axios.get(`${getBackendUrl()}/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fresh = response.data;
      // Use functional setState to read latest state — avoids stale closure overwriting
      // an optimistic increment that may have happened after this call was scheduled.
      setCurrentUser(prev => {
        if (!prev) return prev;
        const updatedUser: AppUser = {
          ...prev,
          // Never decrement — optimistic increment may be ahead of a slow backend response
          total_sessions_completed: Math.max(
            fresh.total_sessions_completed ?? 0,
            prev.total_sessions_completed ?? 0,
          ),
          is_trial: fresh.is_trial ?? prev.is_trial,
          access_end_date: fresh.access_end_date ?? prev.access_end_date,
          access_deadline: fresh.access_deadline ?? prev.access_deadline,
        };
        authCache.currentUser = updatedUser;
        return updatedUser;
      });
      // Bust per-user data cache so next full refresh also gets fresh data
      delete userDataCache[userId];
    } catch (e) {
      // Non-critical — badge will update on next full refresh
    }
  }, [currentUser?.id, token]);

  const incrementSessionCount = useCallback(() => {
    setCurrentUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, total_sessions_completed: (prev.total_sessions_completed ?? 0) + 1 };
      authCache.currentUser = updated;
      return updated;
    });
  }, []);

  const patchUserData = (patch: Partial<AppUser>) => {
    setUserData((prev: any) => {
      const updated = { ...prev, ...patch };
      authCache.userData = updated;
      if (currentUser?.id) {
        const cached = userDataCache[currentUser.id];
        if (cached) cached.data.userData = updated;
      }
      return updated;
    });
  };

  // isTrialExhausted below compares the deadline to the clock, but only when this provider
  // renders — and nothing re-renders it just because time passed. Schedule one render for
  // the moment access ends, so a user who sits on a page past their deadline sees Upgrade
  // instead of a 403 from the session gate. Beyond setTimeout's ~24.8-day ceiling the tab
  // will have re-fetched the user long before it matters.
  const accessDeadline = currentUser?.access_deadline ?? currentUser?.access_end_date;
  const [, setDeadlineTick] = useState(0);
  useEffect(() => {
    if (!accessDeadline) return;
    const msLeft = new Date(accessDeadline).getTime() - Date.now();
    if (msLeft <= 0 || msLeft > MAX_TIMEOUT_MS) return;
    const timer = setTimeout(() => setDeadlineTick(tick => tick + 1), msLeft + 1000);
    return () => clearTimeout(timer);
  }, [accessDeadline]);

  const value = {
    currentUser,
    token,
    loading,
    logout,
    sendPasswordResetEmail,
    userRole,
    error,
    isOffline,
    userData,
    patchUserData,
    refreshAuth,
    isMagicLinkAuth,
    isMarketplaceUser,
    orgNamePending,
    noOrganization,
    refreshSessionCount,
    incrementSessionCount,
    isTrialUser: !!(currentUser?.is_trial),
    trialSessionsUsed: currentUser?.total_sessions_completed ?? 0,
    trialSessionLimit: TRIAL_SESSION_LIMIT,
    isTrialExhausted: !!(
      // Block once access has run out, checked against the clock on every render so it flips
      // the moment the deadline passes even if currentUser is cached. access_deadline is the
      // server's effective end (access_end_date plus the renewal grace for auto-renewing
      // plans), so a subscriber whose renewal webhook hasn't landed yet isn't shown Upgrade
      // and pushed into buying a second subscription. access_end_date is only a fallback for
      // a response that predates the field.
      (accessDeadline && new Date() > new Date(accessDeadline)) ||
      // Also block trial users who have hit the session cap (no date needed).
      (currentUser?.is_trial && (currentUser?.total_sessions_completed ?? 0) >= TRIAL_SESSION_LIMIT)
    ),
    subscriptionCancelled,
    realRole,
    isProxyActive: !!currentUser?.proxy_active,
    startDemoProxy,
    stopDemoProxy,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
